import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import JiraTaskIcon from "../../../JiraTaskIcon";

export type JiraIssue = {
  id: string | number;
  issueKey: string;
  summary: string;
  issueType: "Story" | "Epic" | "Task" | "Subtask" | string;
  status: string;
  statusCategory: "TODO" | "IN_PROGRESS" | "DONE" | string;
  assigneeName?: string;
  assigneeAvatar?: string;
};

export interface IssuesSectionProps {
  title?: string;
  action?: React.ReactNode;
  issues: JiraIssue[];
  onOpen?: (issueKey: string) => void;
  onUnlink?: (id: string | number) => void;
}

function StatusBadge({ status, category }: { status: string; category: string }) {
  const base = "inline-flex items-center rounded-full px-[3px] py-[1px]";
  const textBase = "text-[10px] font-medium leading-[16px] px-[2.5px]";

  if (category === "DONE") {
    return (
      <div className={`${base} bg-green-100`}>
        <span className={`${textBase} text-green-800`}>{status}</span>
      </div>
    );
  }
  if (category === "IN_PROGRESS") {
    return (
      <div className={`${base} bg-teal-100`}>
        <span className={`${textBase} text-teal-600`}>{status}</span>
      </div>
    );
  }
  // TODO - soft dark
  return (
    <div className={`${base} bg-gray-100`}>
      <span className={`${textBase} text-gray-600`}>{status}</span>
    </div>
  );
}

function IssueKeyBadge({ issueKey }: { issueKey: string }) {
  // Inter:Medium 10px, outlined blue badge
  return (
    <div className="inline-flex items-center rounded-full border border-blue-300 px-[3px] py-[1px] shrink-0">
      <span className="text-[10px] font-medium leading-[16px] text-blue-600 px-[2.5px] whitespace-nowrap">
        {issueKey}
      </span>
    </div>
  );
}

export default function IssuesSection({
  title = "JIRA Issues",
  action,
  issues,
  onOpen,
  onUnlink,
}: IssuesSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="w-full flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between pb-2">
        {/* Inter:Semi_Bold 18px, line-height normal, letter-spacing 0.09px */}
        <h2 className="text-[18px] font-semibold leading-normal text-gray-900">{title}</h2>
        {action}
      </div>

      {/* List */}
      <div className="flex flex-col gap-5">
        {issues.map((issue) => (
          <div key={issue.id} className="group relative flex items-center gap-1 w-full">
            {/* Issue Icon, Key, Summary */}
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                <JiraTaskIcon className="w-5 h-5" />
              </div>
              <IssueKeyBadge issueKey={issue.issueKey} />
              {/* Inter:Medium 12px, line-height 16px, letter-spacing 0.06px */}
              <p
                className="text-xs font-medium leading-[16px] text-gray-900 truncate cursor-pointer hover:underline"
                onClick={() => onOpen?.(issue.issueKey)}
                title={issue.summary}
              >
                {issue.summary}
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center flex-1 min-w-0">
              <StatusBadge status={issue.status} category={issue.statusCategory} />
            </div>

            {/* Assignee */}
            <div className="flex items-center gap-1 flex-1 min-w-0">
              {issue.assigneeAvatar ? (
                <img src={issue.assigneeAvatar} alt="" className="w-5 h-5 rounded-full shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0" />
              )}
              {/* Inter:Medium 12px, line-height 16px, letter-spacing 0.06px */}
              <p className="text-xs font-medium leading-[16px] text-gray-900 truncate">
                {issue.assigneeName || t("Unassigned")}
              </p>
            </div>

            {/* Actions (show on hover) */}
            <div className="absolute right-0 top-0 flex items-center gap-2 bg-white rounded-md shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => onUnlink?.(issue.id)}
                className="p-0 hover:bg-gray-100 rounded transition-colors"
                aria-label="Unlink"
                title="Unlink"
              >
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            </div>
          </div>
        ))}
        {issues.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-600">{t("No issues linked")}</div>
        )}
      </div>
    </div>
  );
}
